import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Heading,
  Link,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import MobileMenuWrapper from "../../MobileMenuWrapper/MobileMenuWrapper";
import SearchInput from "../../SearchInput/SearchInput";
import {
  AllBoosIcon,
  HandgunIcon,
  RifleIcon,
  RimfireIcon,
  ShotgunIcon,
} from "../../../assets/icons/icons";
import useUpdateQuickNavigate from "../../../hooks/useUpdateQuickNavigate";
import { getCurrentColor } from "../../../constants";

const categories = [
  { name: "Handgun", icon: <HandgunIcon /> },
  { name: "Rifle", icon: <RifleIcon /> },
  { name: "Rimfire", icon: <RimfireIcon /> },
  { name: "Shotgun", icon: <ShotgunIcon /> },
  { name: "All categories", icon: <AllBoosIcon /> },
];

const QuickNavigateMobile = ({ showNavigate, handleChangeStatus }) => {
  const { colorMode } = useColorMode();
  const { filteredCalibers, loading } = useUpdateQuickNavigate("");
  const location = useLocation();

  // Determine current view type from URL (ls = listings, pd = products)
  const currentViewType = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    // Default to 'ls' if on listings view, otherwise keep 'pd'
    return firstSegment === "ls" ? "ls" : "pd";
  }, [location.pathname]);

  // Helper to build URL with current view type
  const buildCaliberUrl = (caliberSlug) => {
    let modifiedSlug = caliberSlug;
    
    // Remove leading slash for consistent processing
    const slugWithoutLeadingSlash = caliberSlug.replace(/^\//, '');
    
    // Check if slug starts with ls/ or pd/
    if (slugWithoutLeadingSlash.startsWith('ls/') || slugWithoutLeadingSlash.startsWith('pd/')) {
      // Replace the existing prefix with current view type
      modifiedSlug = '/' + slugWithoutLeadingSlash.replace(/^(ls|pd)\//, `${currentViewType}/`);
    } else {
      // No prefix found, add the current view type prefix
      modifiedSlug = `/${currentViewType}/${slugWithoutLeadingSlash}`;
    }
    
    return `${modifiedSlug}?sort_by=landed_cpr&page=1`;
  };

  const RenderAccortionItem = ({ category, calibersData }) => {
    const [search, setSearch] = useState("");
    const [filteredCalibersNew, setFilteredCalibers] = useState(calibersData);

    const { name, icon } = category;

    useEffect(() => {
      sortCalibers(search);
      // eslint-disable-next-line
    }, [search]);

    const sortCalibers = (search) => {
      if (search.length === 0) {
        setFilteredCalibers(calibersData);
      } else {
        const filteredCalibers = calibersData.calibers.filter((caliber) =>
          caliber.caliber.toLowerCase().includes(search.toLowerCase()),
        );

        const filteredData = {
          ...calibersData,
          calibers: filteredCalibers,
        };

        setFilteredCalibers(filteredData);
      }
    };

    return (
      <AccordionItem key={name} border={"none"}>
        <AccordionButton
          bg={getCurrentColor(colorMode, "grayC.600", "grayC.1700")}
          _hover={{ bg: getCurrentColor(colorMode, "grayC.600", "grayC.1700") }}
        >
          <Flex
            gap={"18px"}
            as="span"
            flex="1"
            alignItems={"center"}
            textAlign="left"
          >
            {icon}
            {name}
          </Flex>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel
          pb={4}
          pt={"18px"}
          overflow={"auto"}
          maxH={"400px"}
          bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1900")}
        >
          <SearchInput
            value={search}
            handleChange={setSearch}
            display={"block"}
            pl={"42px"}
            border={"1px solid"}
            borderColor={"grayC.500"}
          />

          <Flex flexDirection={"column"}>
            <Heading
              mt={"18px"}
              fontSize={"sm"}
              color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
              fontFamily={"mainFont"}
              fontWeight={"600"}
              mb={"18px"}
            >
              All Calibers
            </Heading>

            <Flex flexDirection={"column"} gap={"14px"}>
              {!loading && filteredCalibersNew
                ? filteredCalibersNew.calibers &&
                  filteredCalibersNew.calibers.length > 0
                  ? filteredCalibersNew.calibers.map((item, index) => {
                      return (
                        <Link
                          href={buildCaliberUrl(item.caliber_slug)}
                          display={"flex"}
                          justifyContent={"space-between"}
                          key={item + index}
                          color={getCurrentColor(
                            colorMode,
                            "grayC.200",
                            "grayC.1600",
                          )}
                        >
                          <Text
                            fontSize={"sm"}
                            color={getCurrentColor(
                              colorMode,
                              "grayC.200",
                              "grayC.1600",
                            )}
                            fontFamily={"mainFont"}
                            fontWeight={"500"}
                          >
                            {item.caliber}
                          </Text>
                          <Text
                            fontSize={"13"}
                            color={getCurrentColor(
                              colorMode,
                              "grayC.200",
                              "grayC.1600",
                            )}
                            fontFamily={"mainFont"}
                            fontWeight={"400"}
                          >
                            {item.total_count}
                          </Text>
                        </Link>
                      );
                    })
                  : "Calibers not found"
                : "Loading..."}
            </Flex>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    );
  };

  return (
    <MobileMenuWrapper
      openStatus={showNavigate}
      handleChangeStatus={handleChangeStatus}
      title={"Quick Navigate"}
    >
      <Accordion allowMultiple>
        {categories.map((category, i) => (
          <RenderAccortionItem
            category={category}
            calibersData={filteredCalibers[i]}
            key={category.name}
          />
        ))}
      </Accordion>
    </MobileMenuWrapper>
  );
};

export default QuickNavigateMobile;
